/**
 * Password validation utilities
 * Per AUTHENTICATION.md section 3.1:
 * - Minimum length: 12 characters
 * - Maximum length: 128 characters
 * - Character classes: 3 of 4 (uppercase, lowercase, numbers, special)
 */

/** Minimum password length per security requirements */
export const MIN_PASSWORD_LENGTH = 12;

/** Maximum password length per security requirements */
export const MAX_PASSWORD_LENGTH = 128;

export interface PasswordValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Validate password complexity requirements
 * Returns detailed errors for user feedback
 */
export function validatePasswordComplexity(
  password: string
): PasswordValidationResult {
  const errors: string[] = [];

  // Length checks
  if (password.length < MIN_PASSWORD_LENGTH) {
    errors.push(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
  }

  if (password.length > MAX_PASSWORD_LENGTH) {
    errors.push(`Password must be at most ${MAX_PASSWORD_LENGTH} characters`);
  }

  // Character class checks
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumbers = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password);

  const classCount = [hasUppercase, hasLowercase, hasNumbers, hasSpecial].filter(
    Boolean
  ).length;

  if (classCount < 3) {
    errors.push(
      "Password must contain at least 3 of: uppercase, lowercase, numbers, special characters"
    );
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Generate a secure random password
 * Uses crypto.getRandomValues() for cryptographic randomness
 * Useful for initial admin setup or password resets
 */
export function generateSecurePassword(length: number = 16): string {
  const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lowercase = "abcdefghijklmnopqrstuvwxyz";
  const numbers = "0123456789";
  const special = "!@#$%^&*()_+-=";
  const all = uppercase + lowercase + numbers + special;

  function secureRandomIndex(max: number): number {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return array[0] % max;
  }

  // Ensure at least one from each class
  const chars = [
    uppercase[secureRandomIndex(uppercase.length)],
    lowercase[secureRandomIndex(lowercase.length)],
    numbers[secureRandomIndex(numbers.length)],
    special[secureRandomIndex(special.length)],
  ];

  // Fill the rest randomly
  for (let i = chars.length; i < length; i++) {
    chars.push(all[secureRandomIndex(all.length)]);
  }

  // Fisher-Yates shuffle with secure randomness
  for (let i = chars.length - 1; i > 0; i--) {
    const j = secureRandomIndex(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }

  return chars.join("");
}
